'use client';
import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { createClient } from '@/lib/supabase/client';
import { forgeGoalsService } from '@/lib/services/forgeGoalsService';

const ForgeScoreRing = dynamic(() => import('@/components/ui/ForgeScoreRing'), { ssr: false });

export default function ForgeScorePanel() {
  const [todayScore, setTodayScore] = useState<number | null>(null);
  const [streak, setStreak] = useState(0);
  const [goalsSet, setGoalsSet] = useState(false);
  const [scoreBreakdown, setScoreBreakdown] = useState([
    { label: 'Calories On Target', earned: 0, max: 30 },
    { label: 'Protein On Target', earned: 0, max: 25 },
    { label: 'Steps Target', earned: 0, max: 20 },
    { label: 'Training Done', earned: 0, max: 15 },
    { label: 'Sleep Goal', earned: 0, max: 10 },
  ]);

  useEffect(() => {
    const supabase = createClient();
    supabase?.auth?.getUser()?.then(async ({ data: { user } }) => {
      if (!user) return;
      const profile = await forgeGoalsService?.getUserProfile(user?.id);
      if (!profile?.goals_setup_complete) return;
      setGoalsSet(true);
      setStreak(profile?.current_streak || 0);

      const today = new Date()?.toISOString()?.split('T')?.[0];
      const { data: checkin } = await supabase?.from('forge_daily_checkins')?.select('*')?.eq('user_id', user?.id)?.eq('checkin_date', today)?.single();

      if (checkin) {
        setTodayScore(checkin?.forge_score_earned || 0);
        setScoreBreakdown([
          { label: 'Calories On Target', earned: checkin?.nutrition_compliant ? 30 : 0, max: 30 },
          { label: 'Protein On Target', earned: checkin?.nutrition_compliant ? 25 : 0, max: 25 },
          { label: 'Steps Target', earned: 0, max: 20 },
          { label: 'Training Done', earned: checkin?.training_compliant ? 15 : 0, max: 15 },
          { label: 'Sleep Goal', earned: checkin?.sleep_compliant ? 10 : 0, max: 10 },
        ]);
      } else {
        setTodayScore(0);
      }
    });
  }, []);

  const today = new Date()?.toLocaleDateString('en-ZA', { day: '2-digit', month: 'long', year: 'numeric' });

  if (!goalsSet) {
    return (
      <div className="card-forge rounded-xl p-5">
        <div className="text-forge-label mb-4">Forge Score</div>
        <div className="text-center py-8">
          <div className="text-3xl mb-3">⚡</div>
          <div className="text-xs font-bold text-foreground mb-1">No Score Yet</div>
          <div className="text-2xs text-muted-foreground">Complete goal setup to start earning your Forge Score</div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-forge rounded-xl p-5">
      <div className="text-forge-label mb-4">Forge Score</div>
      <div className="flex flex-col items-center mb-5">
        <ForgeScoreRing score={todayScore} size={140} />
        <div className="text-xs text-muted-foreground mt-2">{today}</div>
        {todayScore === 0 && (
          <div className="text-2xs text-muted-foreground/60 mt-1">Complete a Check-In to earn points</div>
        )}
      </div>
      <div className="space-y-2.5">
        {scoreBreakdown?.map((item) => (
          <div key={`score-${item?.label}`} className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{item?.label}</span>
            <div className="flex items-center gap-2">
              <div className="w-16 macro-bar-track">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${(item?.earned / item?.max) * 100}%`,
                    backgroundColor: item?.earned === item?.max ? 'var(--primary)' : item?.earned > 0 ? 'var(--accent)' : 'var(--border)',
                  }}
                />
              </div>
              <span className="text-xs font-bold font-tabular text-foreground w-10 text-right">
                {item?.earned}/{item?.max}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-3 border-t border-border grid grid-cols-2 gap-2 text-center">
        <div>
          <div className="text-forge-label">Today</div>
          <div className="font-tabular font-black text-lg text-primary">{todayScore}</div>
        </div>
        <div>
          <div className="text-forge-label">Streak</div>
          <div className="font-tabular font-black text-lg text-foreground">{streak} days</div>
        </div>
      </div>
    </div>
  );
}